import { GTMEvent } from '@airglobal/pylot-gtm/src/GTM'
import { formatPrice } from '@corratech/pylot-seo'
import { itemHasKeys } from '@corratech/pylot-utils'
import { CartPrices, AppliedCoupon } from '@pylot-data/pylotschema'
import { NextRouter } from 'next/router'
import { UpsellPromotionAdditionalData } from 'stores/ooka_uae_store/components/OOKA/pylot-gtm/src/GTMEvents/pushUpsellPromotionView'

type GTMCartItemData = {
    id: string
    name: string
    price: number
    quantity: number
    currencyCode: string
    category?: string
    variant?: string
    brand?: string
    value?: number
    subscription_frequency?: string
    creative_name?: string
    creative_slot?: string
    promotion_id?: string
    promotion_name?: string
}

type localItemType = {
    sku: string
    quantity: number
}

const mandatoryCartItemProps = [
    'id',
    'name',
    'price',
    'quantity',
    'currencyCode'
]

const isValidGTMCartItems = (items: unknown): items is GTMCartItemData[] => {
    if (!Array.isArray(items)) {
        return false
    }
    let result = true
    for (const item of items) {
        if (item && !itemHasKeys(item, mandatoryCartItemProps)) {
            result = false
        }
    }
    return result
}

const EVENT_NAME_MAP = {
    add: 'addToCart',
    remove: 'removeFromCart'
}

const isCategoryPath = (asPath: string) => asPath.includes('/c/')
const isProductPath = (asPath: string) => asPath.includes('/p/')

const getCategoryPath = () => {
    const categoryName = localStorage.getItem('Category')
    return `Category - ${categoryName}`
}

const handleProductPath = (
    asPath: string,
    promotionBannerName: string | null
) => {
    const previousPath = sessionStorage.getItem('prevPath')

    if (isCategoryPath(previousPath!)) {
        return getCategoryPath()
    } else if (promotionBannerName) {
        return promotionBannerName
    } else {
        return asPath
    }
}

const getPath = (router: NextRouter) => {
    const { asPath } = router
    const promotionBannerName = localStorage.getItem('promotionBanner')

    if (promotionBannerName) {
        return promotionBannerName
    }

    if (isCategoryPath(asPath)) {
        return getCategoryPath()
    }

    if (isProductPath(asPath)) {
        return handleProductPath(asPath, promotionBannerName)
    }

    return asPath
}

export const formatCartGTMEvent = (
    data: Record<string, unknown>,
    event: 'add' | 'remove',
    router: NextRouter
): GTMEvent => {
    const {
        updatedItems,
        productsDetails,
        applied_coupons,
        items,
        prices
    } = data
    if (!isValidGTMCartItems(updatedItems)) {
        console.info(`Invalid GTM ${event} cart item data`)
        return { event: '' }
    }

    const getBrandBySku = (sku: string): string => {
        if (Array.isArray(items)) {
            const matchingCartItem = items.find(
                (cartItem) => cartItem?.product?.sku === sku
            )
            if (matchingCartItem?.product?.brand_text) {
                return String(matchingCartItem.product.brand_text)
            }
            if (matchingCartItem?.product?.brand) {
                return String(matchingCartItem.product.brand)
            }
        }
        if (Array.isArray(productsDetails)) {
            const matchingProduct = productsDetails.find(
                (product) => product?.id === sku
            )
            if (matchingProduct?.brand) {
                return String(matchingProduct.brand)
            }
        }
        return ''
    }

    const mappedUpdatedItems = updatedItems.map((item) => {
        return {
            ...item,
            price: Number(item.price),
            brand: item.brand || getBrandBySku(String(item.id))
        }
    })

    const updatedProductDetails =
        productsDetails && Array.isArray(productsDetails)
            ? productsDetails.map((product) => {
                  if (!Array.isArray(items)) {
                      return product
                  }
                  const matchingItem = items.find(
                      (item) => item.product.sku === product.id
                  )

                  if (matchingItem) {
                      return {
                          ...product,
                          subscription_frequency:
                              matchingItem.subscription_data?.billing_period ??
                              'NA',
                          brand:
                              matchingItem.product?.brand_text ??
                              matchingItem.product?.brand ??
                              product.brand ??
                              ''
                      }
                  }

                  return product
              })
            : []
    const removedProductPrice = updatedItems.map((item) => {
        const removedItemPrice =
            Number(formatPrice(item.price)) * Number(item.quantity)
        return removedItemPrice
    })

    const currencyCode = updatedItems[0].currencyCode

    const products =
        updatedProductDetails && Array.isArray(updatedProductDetails)
            ? updatedProductDetails
            : []
    // Reorder cart items
    products.reverse()

    const localItems =
        JSON.parse(localStorage.getItem('addedToCartItems') || '[]') || []

    const match = (productId: string) => {
        return localItems.find(
            (localItem: localItemType) => localItem.sku === productId
        )
    }

    const mappedCartProducts = products.map((product) => {
        if (match(product.id)) {
            return {
                ...product,
                ...UpsellPromotionAdditionalData,
                price: Number(product.price)
            }
        }
        return {
            ...product,
            price: Number(product.price)
        }
    })

    if (EVENT_NAME_MAP[event] === 'removeFromCart') {
        return {
            event: EVENT_NAME_MAP[event],
            ecommerce: {
                currencyCode,
                [event]: {
                    value: removedProductPrice[0],
                    coupon:
                        (applied_coupons as AppliedCoupon[])?.[0]?.code || '',
                    discount:
                        (prices as CartPrices)?.discounts?.[0]?.amount?.value ??
                        0,
                    products: mappedUpdatedItems
                }
            },
            cartContents: {
                currencyCode,
                products: mappedCartProducts
            },
            _clear: true
        }
    }
    const mappedUpdatedAddedItems = updatedItems.map((item) => {
        const matchingProduct = products.find(
            (product) => product.id === item.id
        )
        const productPrice = matchingProduct
            ? matchingProduct.price
            : item.price

        const matchedItem = match(item.id)
        if (
            match(item.id) &&
            matchedItem.quantity >= matchingProduct.quantity
        ) {
            return {
                ...item,
                ...UpsellPromotionAdditionalData,
                price: Number(productPrice),
                brand: matchingProduct?.brand ?? item.brand ?? '',
                list: getPath(router)
            }
        }
        return {
            ...item,
            price: Number(productPrice),
            brand: matchingProduct?.brand ?? item.brand ?? '',
            list: getPath(router)
        }
    })
    return {
        event: EVENT_NAME_MAP[event],
        ecommerce: {
            currencyCode,
            [event]: {
                products: mappedUpdatedAddedItems
            }
        },
        cartContents: {
            currencyCode,
            products: mappedCartProducts
        },
        _clear: true
    }
}

export const pushAddToCart = (
    data: Record<string, unknown>,
    router: NextRouter
): GTMEvent => {
    return formatCartGTMEvent(data, 'add', router)
}

export const pushRemoveFromCart = (
    data: Record<string, unknown>,
    router: NextRouter
): GTMEvent => {
    return formatCartGTMEvent(data, 'remove', router)
}
